// Buscaminas: una sola dificultad para todos (16 × 16, 40 minas). Con sesión iniciada, el tiempo va al ranking.
// Se juega al momento: el primer toque crea la partida (siempre es seguro) y pone en marcha el reloj.
// El servidor reparte la semilla con ese primer toque y repite la partida al terminar para validarla.
// El tablero se ajusta al alto de la pantalla para verlo entero sin hacer scroll.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeftIcon, QuestionMarkCircleIcon, TrophyIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import GameIcon from '../components/GameIcon';
import Modal from '../components/Modal';
import GameGuide from '../components/GameGuide';
import MinesBoard, { FlagMark, MineMark, type CellAction } from '../games/minesweeper/MinesBoard';
import { applyMove, createBoard, flagsUsed, newGame, BOARD, type GameState, type Move } from '../games/minesweeper/engine';
import { formatClock, formatTime } from '../games/minesweeper/format';
import { useAuthStore } from '../store/authStore';
import { applyXpUpdate, countGamePlay, type XpUpdate } from '../lib/results';
import { API_URL } from '../lib/api';

type Phase = 'ready' | 'loading' | 'playing' | 'won' | 'lost';

interface Session {
  gameId: string | null;   // null: partida local que no cuenta para el ranking
  startedAt: number;       // performance.now() del primer toque
}

interface FinishData {
  result: 'win' | 'loss';
  timeMs: number;
  personalBest: number | null;
  newRecord: boolean;
  rank: number | null;
  xp: XpUpdate | null;
}

const RULES = [
  { title: 'El objetivo', text: 'Descubre todas las casillas que no tienen mina. El primer toque nunca es mina y pone en marcha el reloj.' },
  { title: 'Los números', text: 'Cada número dice cuántas minas hay en las ocho casillas que lo rodean.' },
  { title: 'Las banderas', text: 'Clic derecho (o pulsación larga en el móvil) marca una casilla con bandera. También puedes activar el modo bandera.' },
  { title: 'Abrir alrededor', text: 'Toca un número que ya tenga todas sus banderas puestas para abrir de golpe las casillas que lo rodean.' },
  { title: 'El ranking', text: 'Todo el mundo juega la misma dificultad: 16 × 16 con 40 minas. Cuenta tu mejor tiempo.' },
];

function randomSeed() {
  const bytes = new Uint8Array(12);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

// Tablero tapado antes del primer toque (las minas aún no existen)
const CENTER = Math.floor(BOARD.rows / 2) * BOARD.cols + Math.floor(BOARD.cols / 2);
function coveredState(): GameState {
  const total = BOARD.cols * BOARD.rows;
  return {
    board: { cfg: BOARD, mines: new Array(total).fill(false), counts: new Array(total).fill(0), first: CENTER },
    open: new Array(total).fill(false),
    flags: new Array(total).fill(false),
    opened: 0,
    status: 'playing',
    exploded: -1,
  };
}

export default function Minesweeper() {
  const { isAuthenticated, user } = useAuthStore();

  const [phase, setPhase] = useState<Phase>('ready');
  const [game, setGame] = useState<GameState | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const moves = useRef<Move[]>([]);
  const [endMs, setEndMs] = useState(0);
  const [now, setNow] = useState(0);
  const [flagMode, setFlagMode] = useState(false);
  const [finish, setFinish] = useState<FinishData | null>(null);
  const [saveError, setSaveError] = useState('');
  const [startError, setStartError] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [confirmRestart, setConfirmRestart] = useState(false);

  // Espacio disponible (ancho del contenedor y alto de pantalla bajo la cabecera) para el tamaño de casilla
  const wrapRef = useRef<HTMLDivElement>(null);
  const [space, setSpace] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const top = el.getBoundingClientRect().top + window.scrollY;
      setSpace({ width: el.clientWidth, height: window.innerHeight - top });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
  }, []);

  const cfg = BOARD;
  const narrow = space.width < 600;
  const gap = narrow ? 2 : 3;
  const pad = narrow ? 12 : 24;     // p-1.5 / sm:p-3 del panel, a ambos lados
  const below = 44;                 // línea de ayuda bajo el tablero y margen inferior
  const byWidth = Math.floor((space.width - pad - gap * (cfg.cols - 1)) / cfg.cols);
  const byHeight = Math.floor((space.height - below - pad - gap * (cfg.rows - 1)) / cfg.rows);
  const size = Math.max(18, Math.min(40, byWidth, byHeight));

  // Reloj
  useEffect(() => {
    if (phase !== 'playing') return;
    let raf = 0;
    let last = -1;
    const tick = () => {
      const s = Math.floor(performance.now() / 250);
      if (s !== last) { last = s; setNow(performance.now()); }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [phase]);

  // Avisar antes de salir con una partida a medias que cuenta
  useEffect(() => {
    if (phase !== 'playing' || !session?.gameId) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase, session]);

  // Vuelve al tablero tapado: la siguiente partida empieza con el primer toque
  const reset = () => {
    moves.current = [];
    setGame(null);
    setSession(null);
    setFinish(null);
    setSaveError('');
    setStartError('');
    setShowResult(false);
    setFlagMode(false);
    setPhase('ready');
  };

  // Primer toque: pide la semilla al servidor (con sesión), crea el tablero y arranca el reloj
  const begin = async (first: number) => {
    setPhase('loading');
    let seed = randomSeed();
    let gameId: string | null = null;
    if (isAuthenticated) {
      try {
        const { data } = await axios.post(`${API_URL}/api/minesweeper/start`);
        seed = data.data.seed;
        gameId = data.data.gameId;
      } catch {
        // Sin conexión con el servidor: se puede jugar igual, pero no cuenta
        setStartError('Sin conexión con el servidor: esta partida no contará para el ranking.');
      }
    }
    moves.current = [{ a: 'r', i: first, t: 0 }];
    setGame(newGame(createBoard(BOARD, seed, first)));
    setSession({ gameId, startedAt: performance.now() });
    setNow(performance.now());
    setPhase('playing');
    countGamePlay('minesweeper');
  };

  const restart = () => (phase === 'playing' && moves.current.length > 1 ? setConfirmRestart(true) : reset());

  const submit = async (gameId: string) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/minesweeper/finish`, { gameId, moves: moves.current });
      setFinish(data.data);
      applyXpUpdate(data.data.xp);
    } catch (err: any) {
      setSaveError(err.response?.data?.error ?? 'No se ha podido guardar la partida.');
    }
  };

  const onAction = (a: CellAction, i: number) => {
    if (phase === 'ready') { if (a === 'r') begin(i); return; }
    if (phase !== 'playing' || !game || !session) return;
    const t = Math.max(0, Math.round(performance.now() - session.startedAt));
    const next = applyMove(game, { a, i, t });
    if (next === game) return; // movimiento sin efecto: no se registra
    moves.current.push({ a, i, t });
    setGame(next);
    if (next.status !== 'playing') {
      setEndMs(t);
      setPhase(next.status);
      if (session.gameId) submit(session.gameId);
      // Un momento para ver el tablero antes del resultado
      window.setTimeout(() => setShowResult(true), next.status === 'won' ? 600 : 1100);
    }
  };

  const ended = phase === 'won' || phase === 'lost';
  const elapsed = phase === 'playing' && session ? now - session.startedAt : ended ? endMs : 0;
  const minesLeft = cfg.mines - (game ? flagsUsed(game) : 0);
  const counted = !!session?.gameId;

  return (
    <>
    <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-3 sm:pt-5 pb-6">
      {/* Cabecera y marcador en una sola fila para dejar el alto al tablero */}
      <div className="flex items-center gap-1 sm:gap-3 max-w-3xl mx-auto">
        <Link to="/" className="btn-plain -ml-3 h-9 px-2 text-footnote shrink-0" aria-label="Volver a juegos">
          <ChevronLeftIcon className="w-5 h-5" />
        </Link>
        <GameIcon id="minesweeper" size={36} shared />
        <div className="hidden sm:block leading-tight min-w-0 ml-1">
          <h1 className="text-title3 font-semibold text-ink">Buscaminas</h1>
          <p className="text-caption text-ink-2 tabular truncate">{cfg.cols} × {cfg.rows} · {cfg.mines} minas</p>
        </div>
        <h1 className="sr-only sm:hidden">Buscaminas</h1>

        <div className="flex-1 flex items-center justify-center gap-4 sm:gap-6">
          <span className="flex items-center gap-1 text-title3 font-semibold text-ink tabular" aria-label={`${minesLeft} minas por marcar`}>
            <FlagMark className="w-5 h-5 text-danger" /> {minesLeft}
          </span>
          <span className="text-title3 font-semibold text-ink tabular min-w-[3.5rem] text-center" aria-label="Tiempo">
            {ended ? formatTime(elapsed) : formatClock(elapsed)}
          </span>
        </div>

        <button
          type="button"
          onClick={() => setFlagMode((f) => !f)}
          disabled={phase !== 'playing'}
          aria-pressed={flagMode}
          aria-label="Modo bandera"
          title="Modo bandera"
          className={`w-9 h-9 shrink-0 rounded-full grid place-items-center transition-colors disabled:opacity-40 ${flagMode ? 'bg-danger/10 text-danger' : 'text-ink-2 hover:text-ink hover:bg-ink/[0.05]'}`}
        >
          <FlagMark className="w-5 h-5" />
        </button>
        <button
          type="button"
          onClick={restart}
          disabled={phase === 'loading' || phase === 'ready'}
          aria-label="Nueva partida"
          title="Nueva partida"
          className="w-9 h-9 shrink-0 rounded-full grid place-items-center text-ink-2 hover:text-ink hover:bg-ink/[0.05] transition-colors disabled:opacity-40"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </button>
        <button onClick={() => setShowRules(true)} className="hidden sm:grid w-9 h-9 shrink-0 rounded-full place-items-center text-ink-2 hover:text-ink hover:bg-ink/[0.05] transition-colors" aria-label="Cómo se juega">
          <QuestionMarkCircleIcon className="w-6 h-6" />
        </button>
        <Link to="/ranking" className="w-9 h-9 shrink-0 rounded-full grid place-items-center text-ink-2 hover:text-ink hover:bg-ink/[0.05] transition-colors" aria-label="Ranking">
          <TrophyIcon className="w-6 h-6" />
        </Link>
      </div>

      {/* Tablero */}
      <div ref={wrapRef} className="mt-3 sm:mt-4 -mx-4 sm:mx-0">
        <div className="overflow-x-auto">
          <div
            className="relative mx-auto w-max rounded-[1.25rem] sm:rounded-3xl bg-surface p-1.5 sm:p-3 shadow-card"
            style={{ backgroundImage: 'linear-gradient(rgb(var(--game-minesweeper) / 0.06), rgb(var(--game-minesweeper) / 0.06))' }}
          >
            <MinesBoard state={game ?? coveredState()} size={size} gap={gap} flagMode={flagMode} onAction={onAction} />

          </div>
        </div>
      </div>

      {/* Ayuda y estado bajo el tablero (una línea, cabe en el hueco reservado) */}
      <div className="mt-2.5 min-h-[2rem] max-w-xl mx-auto flex items-center justify-center gap-2 text-center">
        {(phase === 'ready' || phase === 'loading') && (
          <p className="text-footnote text-ink-2">
            {phase === 'loading' ? 'Preparando el tablero…' : <><strong className="text-ink font-semibold">Toca cualquier casilla para empezar.</strong>{' '}</>}
            {phase === 'ready' && !isAuthenticated && (
              <span className="hidden sm:inline"><Link to="/auth?tab=register" className="text-accent font-medium hover:underline">Crea una cuenta</Link> para entrar en el ranking.</span>
            )}
            {phase === 'ready' && isAuthenticated && user?.isGuest && <span className="hidden sm:inline text-ink-3">Como invitado no sales en el ranking.</span>}
          </p>
        )}
        {phase === 'playing' && startError && <p className="text-caption text-warning">{startError}</p>}
        {phase === 'playing' && !startError && (
          <p className="text-caption text-ink-3">
            <span className="hidden sm:inline">Clic derecho para poner bandera. </span>
            <span className="sm:hidden">Mantén pulsado para poner bandera. </span>
            Toca un número para abrir alrededor.
          </p>
        )}
        {ended && !showResult && (
          <>
            <button onClick={reset} className="btn-primary h-9">Otra partida</button>
            <button onClick={() => setShowResult(true)} className="btn-secondary h-9">Ver resultado</button>
          </>
        )}
      </div>

      {/* Resultado */}
      <Modal open={showResult && ended} onClose={() => setShowResult(false)} labelledBy="mines-result">
        <ResultSheet
          won={phase === 'won'}
          timeMs={endMs}
          counted={counted}
          finish={finish}
          saveError={saveError}
          isAuthenticated={isAuthenticated}
          isGuest={!!user?.isGuest}
          onAgain={reset}
          onReview={() => setShowResult(false)}
        />
      </Modal>

      {/* Reiniciar con una partida a medias */}
      <Modal open={confirmRestart} onClose={() => setConfirmRestart(false)} labelledBy="mines-restart">
        <h2 id="mines-restart" className="text-title3 font-semibold text-ink">¿Empezar otra partida?</h2>
        <p className="mt-2 text-footnote text-ink-2">La partida en curso no contará.</p>
        <div className="mt-6 flex flex-col gap-2">
          <button className="btn-primary w-full" onClick={() => { setConfirmRestart(false); reset(); }}>Empezar otra</button>
          <button className="btn-secondary w-full" onClick={() => setConfirmRestart(false)}>Seguir jugando</button>
        </div>
      </Modal>

      {/* Reglas */}
      <Modal open={showRules} onClose={() => setShowRules(false)} labelledBy="mines-rules">
        <h2 id="mines-rules" className="text-title3 font-semibold text-ink">Cómo se juega</h2>
        <dl className="mt-4 flex flex-col gap-3">
          {RULES.map((r) => (
            <div key={r.title}>
              <dt className="text-footnote font-semibold text-ink">{r.title}</dt>
              <dd className="text-footnote text-ink-2">{r.text}</dd>
            </div>
          ))}
        </dl>
        <button onClick={() => setShowRules(false)} className="btn-secondary w-full mt-6">Entendido</button>
      </Modal>
    </div>
    <GameGuide path="/buscaminas" />
    </>
  );
}

function ResultSheet({ won, timeMs, counted, finish, saveError, isAuthenticated, isGuest, onAgain, onReview }: {
  won: boolean;
  timeMs: number;
  counted: boolean;
  finish: FinishData | null;
  saveError: string;
  isAuthenticated: boolean;
  isGuest: boolean;
  onAgain: () => void;
  onReview: () => void;
}) {
  const saving = counted && !finish && !saveError;
  return (
    <div className="text-center">
      <div className={`mx-auto w-14 h-14 rounded-full grid place-items-center ${won ? 'bg-success/10 text-success' : 'bg-fill text-ink-2'}`}>
        {won ? <TrophyIcon className="w-7 h-7" /> : <MineMark className="w-7 h-7" />}
      </div>
      <h2 id="mines-result" className="mt-4 text-title2 font-bold text-ink">{won ? 'Campo despejado' : 'Has pisado una mina'}</h2>

      {won && <p className="mt-3 text-title1 font-bold text-ink tabular">{formatTime(timeMs)}</p>}

      {won && counted && (
        <div className="mt-4 rounded-2xl bg-fill px-4 py-3 text-footnote text-ink-2 min-h-[3rem] flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          {saving && <span>Guardando…</span>}
          {saveError && <span className="text-danger">{saveError}</span>}
          {finish && (
            <>
              {finish.newRecord
                ? <span className="font-semibold text-success">Nuevo récord personal</span>
                : finish.personalBest !== null && <span>Tu récord: <strong className="text-ink tabular">{formatTime(finish.personalBest)}</strong></span>}
              {finish.rank !== null && <span>Puesto <strong className="text-ink tabular">{finish.rank}</strong></span>}
              {finish.xp && finish.xp.xpAwarded > 0 && <span className="text-accent font-semibold tabular">+{finish.xp.xpAwarded} XP</span>}
            </>
          )}
        </div>
      )}

      {won && !counted && (
        <p className="mt-4 text-footnote text-ink-2">
          {!isAuthenticated
            ? <><Link to="/auth?tab=register" className="text-accent font-medium hover:underline">Crea una cuenta</Link> para guardar este tiempo y entrar en el ranking.</>
            : 'Esta partida no se ha guardado.'}
        </p>
      )}
      {won && counted && isGuest && <p className="mt-2 text-caption text-ink-2">Como invitado no apareces en el ranking.</p>}

      <div className="mt-6 flex flex-col gap-2">
        <button onClick={onAgain} className="btn-primary w-full">Otra partida</button>
        <Link to="/ranking" className="btn-secondary w-full">Ver ranking</Link>
        <button onClick={onReview} className="btn-plain w-full">Ver el tablero</button>
      </div>
    </div>
  );
}
